import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as HideIcon } from '@repo/common/assets/icons/16px/icon_16px_invisible-close_outline.svg';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/16px/icon_16px_clock-alarm_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/16px/icon_16px_copy.svg';
import { ReactComponent as RocketLaunchIcon } from '@repo/common/assets/icons/16px/icon_16px_rocket-launch_outline.svg';
import { ReactComponent as UndoIcon } from '@repo/common/assets/icons/16px/icon_16px_undo-left.svg';

import { BaseButton, useT } from '@repo/common';

const MENU_WIDTH = 200;
const MARGIN = 8;

const DELETED_OPTIONS = [
  { key: 'restore', label: ['Restore', '복원'], icon: <UndoIcon /> },
  {
    key: 'deletePermanent',
    label: ['Delete permanently', '영구 삭제'],
    icon: <DeleteIcon />,
  },
];

export const OPTION_BY_STATUS = {
  ACTIVE: [
    { key: 'edit', label: ['Edit', '수정'], icon: <EditIcon /> },
    { key: 'hide', label: ['Hide', '숨기기'], icon: <HideIcon /> },
    { key: 'close', label: ['Close', '마감'], icon: <CloseIcon /> },
    { key: 'delete', label: ['Move to Trash', '휴지통으로 이동'], icon: <DeleteIcon /> },
  ],
  HIDDEN: [
    { key: 'setActive', label: ['Set as Active', '게시 상태로 변경'], icon: <VisibleIcon /> },
    { key: 'edit', label: ['Edit', '수정'], icon: <EditIcon /> },
    { key: 'duplicate', label: ['Duplicate', '복제'], icon: <CopyIcon /> },
    { key: 'delete', label: ['Move to Trash', '휴지통으로 이동'], icon: <DeleteIcon /> },
  ],
  DRAFT: [
    { key: 'edit', label: ['Edit', '수정'], icon: <EditIcon /> },
    { key: 'publish', label: ['Publish Job', '공고 게시'], icon: <RocketLaunchIcon /> },
    { key: 'duplicate', label: ['Duplicate', '복제'], icon: <CopyIcon /> },
    { key: 'delete', label: ['Move to Trash', '휴지통으로 이동'], icon: <DeleteIcon /> },
  ],
  CLOSED: [
    { key: 'edit', label: ['Edit', '수정'], icon: <EditIcon /> },
    { key: 'reopen', label: ['Set as Active', '게시 상태로 변경'], icon: <VisibleIcon /> },
    { key: 'delete', label: ['Move to Trash', '휴지통으로 이동'], icon: <DeleteIcon /> },
  ],
  DELETED_FROM_ACTIVE: DELETED_OPTIONS,
  DELETED_FROM_HIDDEN: DELETED_OPTIONS,
  DELETED_FROM_DRAFT: DELETED_OPTIONS,
  DELETED_FROM_CLOSED: DELETED_OPTIONS,
};

function CardOptionList({ anchorRect, onClose, status, onAction }) {
  const t = useT();
  const listRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const options = OPTION_BY_STATUS[status] ?? [];

  useEffect(() => {
    if (!onClose) return;

    const handleScroll = () => {
      onClose();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onClose]);

  useEffect(() => {
    const handleResize = () => onClose?.();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onClose]);

  const [menuWidth, setMenuWidth] = useState(MENU_WIDTH);

  useEffect(() => {
    if (listRef.current) {
      const rect = listRef.current.getBoundingClientRect();
      setMenuWidth(rect.width);
    }
  }, [status, anchorRect]);

  const left = Math.min(
    window.innerWidth - menuWidth - MARGIN,
    anchorRect.right - menuWidth,
  );

  if (!anchorRect) return null;

  const handleItemClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);
    onAction?.(action);
    onClose?.();
  };

  return (
    <>
      {anchorRect &&
        ReactDOM.createPortal(
          <>
            <div
              className='fixed inset-0 z-[999999] bg-transparent'
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
            />

            <div
              ref={listRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: anchorRect.bottom + 6,
                left,
              }}
              className='
                min-w-[160px] max-w-[200px]
                bg-grayscale-gray00 
                border border-grayscale-gray02 
                rounded-lg
                shadow-popup-04
                z-[1000000]
              '
            >
              <div className='bg-grayscale-gray00 rounded-lg p-2'>
                <ul className='flex flex-col'>
                  {options.map((option) => (
                    <li key={option.key}>
                      <BaseButton
                        type='text'
                        size='m'
                        iconPosition='after'
                        icon={option.icon}
                        width='100%'
                        separate={true}
                        leftalign={true}
                        onClick={(e) => handleItemClick(e, option.key)}
                        bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                          activeItem === option.key
                            ? 'bg-grayscale-gray02 text-text-primary'
                            : ''
                        }`}
                        textclass='typo-b14Strong'
                        hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                        activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                      >
                        {t(option.label[0], option.label[1])}
                      </BaseButton>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
export default CardOptionList;
